import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  Building2,
  Handshake,
  Home,
  ListTodo,
  Menu,
  Plus,
  Radar,
  Receipt,
  Settings,
  Truck,
  Users,
} from "lucide-react";
import { CanAccess, useCanAccess, useTranslate } from "ra-core";
import { Link, matchPath, useLocation, useMatch } from "react-router";
import { ContactCreateSheet } from "../contacts/ContactCreateSheet";
import { useState } from "react";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TaskCreateSheet } from "../tasks/TaskCreateSheet";

export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();
  // iTHNK: the delivery role has no tasks; it gets deliveries and invoices instead
  const { canAccess: canAccessTasks = true } = useCanAccess({
    resource: "tasks",
    action: "list",
  });

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/tasks/*", location.pathname)) {
    currentPath = "/tasks";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (matchPath("/deliveries/*", location.pathname)) {
    currentPath = "/deliveries";
  } else if (matchPath("/invoices/*", location.pathname)) {
    currentPath = "/invoices";
  } else if (
    matchPath("/generate/*", location.pathname) ||
    matchPath("/leads/*", location.pathname)
  ) {
    currentPath = "/generate";
  } else if (matchPath("/settings/*", location.pathname)) {
    currentPath = "/settings";
  } else {
    currentPath = false;
  }

  // Check if the app is running as a PWA (standalone mode)
  const isPwa = window.matchMedia("(display-mode: standalone)").matches;
  // Check if it's iOS on the web
  const isWebiOS = /iPad|iPod|iPhone/.test(window.navigator.userAgent);

  return (
    <nav
      aria-label={translate("crm.navigation.label")}
      className="fixed bottom-0 left-0 right-0 z-50 bg-secondary h-14"
      style={{
        // iOS bug: even though viewport is set correctly, the bottom safe area inset is not accounted for
        // So we manually add some padding to avoid the navigation being too close to the home bar
        paddingBottom: isPwa && isWebiOS ? 15 : undefined,
        // We use box-sizing: border-box, so the height contains the padding.
        // To actually increase the padding, we need to increase the height as well
        height:
          "calc(var(--spacing)) * 6" + (isPwa && isWebiOS ? " + 15px" : ""),
      }}
    >
      <div className="flex justify-center">
        <>
          <NavigationButton
            href="/"
            Icon={Home}
            label={translate("ra.page.dashboard")}
            isActive={currentPath === "/"}
          />
          {/* iTHNK: Dashboard, Generate, +, Tasks, Menu; the delivery role has no leads or tasks */}
          {canAccessTasks ? (
            <>
              <CanAccess resource="pre_leads" action="list">
                <NavigationButton
                  href="/generate"
                  Icon={Radar}
                  label={translate("resources.pre_leads.nav")}
                  isActive={currentPath === "/generate"}
                />
              </CanAccess>
              <CreateButton />
              <NavigationButton
                href="/tasks"
                Icon={ListTodo}
                label={translate("resources.tasks.name", { smart_count: 2 })}
                isActive={currentPath === "/tasks"}
              />
            </>
          ) : (
            <>
              <NavigationButton
                href="/deliveries"
                Icon={Truck}
                label={translate("resources.deliveries.name", {
                  smart_count: 2,
                })}
                isActive={currentPath === "/deliveries"}
              />
              <NavigationButton
                href="/invoices"
                Icon={Receipt}
                label={translate("resources.invoices.name", {
                  smart_count: 2,
                })}
                isActive={currentPath === "/invoices"}
              />
            </>
          )}
          <MenuButton currentPath={currentPath} />
        </>
      </div>
    </nav>
  );
};

const NavigationButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Button
    asChild
    variant="ghost"
    className={cn(
      "flex-col gap-1 h-auto py-2 px-1 rounded-md w-16",
      isActive ? null : "text-muted-foreground",
    )}
  >
    <Link to={href}>
      <Icon className="size-6" />
      <span className="text-[0.6rem] font-medium">{label}</span>
    </Link>
  </Button>
);

const CreateButton = () => {
  const translate = useTranslate();
  const contact_id = useMatch("/contacts/:id/*")?.params.id;
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);

  return (
    <>
      <ContactCreateSheet
        open={contactCreateOpen}
        onOpenChange={setContactCreateOpen}
      />
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={contact_id}
      />
      <TaskCreateSheet
        open={taskCreateOpen}
        onOpenChange={setTaskCreateOpen}
        contact_id={contact_id}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="default"
            size="icon"
            className="h-16 w-16 rounded-full -mt-3"
            aria-label={translate("ra.action.create")}
          >
            <Plus className="size-10" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setContactCreateOpen(true);
            }}
          >
            {translate("resources.contacts.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setNoteCreateOpen(true);
            }}
          >
            {translate("resources.notes.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setTaskCreateOpen(true);
            }}
          >
            {translate("resources.tasks.forcedCaseName")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

/** Every section that is not on the bar, as on the desktop header; each one follows the role (canAccess) */
const MENU_ITEMS = [
  { href: "/contacts", resource: "contacts", Icon: Users },
  { href: "/companies", resource: "companies", Icon: Building2 },
  { href: "/deals", resource: "deals", Icon: Handshake },
  { href: "/deliveries", resource: "deliveries", Icon: Truck },
  { href: "/invoices", resource: "invoices", Icon: Receipt },
] as const;

const MenuButton = ({ currentPath }: { currentPath: string | boolean }) => {
  const translate = useTranslate();
  const onBar = ["/", "/generate", "/tasks"];
  const isActive =
    currentPath !== false && !onBar.includes(String(currentPath));
  const label = translate("crm.navigation.menu", { _: "Menu" });
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "flex-col gap-1 h-auto py-2 px-1 rounded-md w-16",
            isActive ? null : "text-muted-foreground",
          )}
        >
          <Menu className="size-6" />
          <span className="text-[0.6rem] font-medium">{label}</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="rounded-t-xl pb-6">
        <SheetHeader className="pb-0">
          <SheetTitle>{label}</SheetTitle>
          <SheetDescription className="sr-only">
            {translate("crm.navigation.label")}
          </SheetDescription>
        </SheetHeader>
        <nav className="flex flex-col px-2">
          {MENU_ITEMS.map(({ href, resource, Icon }) => (
            <CanAccess key={href} resource={resource} action="list">
              <MenuLink
                href={href}
                Icon={Icon}
                label={translate(`resources.${resource}.name`, {
                  smart_count: 2,
                })}
                isActive={currentPath === href}
              />
            </CanAccess>
          ))}
          <MenuLink
            href="/settings"
            Icon={Settings}
            label={translate("crm.settings.title")}
            isActive={currentPath === "/settings"}
          />
        </nav>
      </SheetContent>
    </Sheet>
  );
};

const MenuLink = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <SheetClose asChild>
    <Link
      to={href}
      aria-current={isActive ? "page" : undefined}
      className={cn(
        "flex items-center gap-4 h-12 px-3 rounded-md text-base font-medium",
        isActive ? "bg-accent text-accent-foreground" : "hover:bg-accent/60",
      )}
    >
      <Icon className="size-5 text-muted-foreground" />
      {label}
    </Link>
  </SheetClose>
);
